import { useStoreApi } from '@xyflow/react';
import { useEffect, useRef, useState } from 'react';

/** Keep slide scaling off until fonts, node handles, and the viewport settle. */
export function useEntityDiagramRenderReady({
  isInitialized,
  onReady,
}: {
  isInitialized: boolean;
  onReady?: () => void;
}) {
  const store = useStoreApi();
  const [fontsReady, setFontsReady] = useState(false);
  const hasReportedReady = useRef(false);

  useEffect(() => {
    let cancelled = false;
    void document.fonts.ready.then(() => {
      if (!cancelled) setFontsReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!fontsReady || !isInitialized || !onReady || hasReportedReady.current) {
      return;
    }

    let frame: number;
    let previousGeometry: string | undefined;
    let stableFrames = 0;
    const checkGeometry = () => {
      const { width, height, transform, nodeLookup } = store.getState();
      const nodes = Array.from(nodeLookup.values()).filter(
        (node) => !node.hidden
      );
      // Read the live store, not reducer nodes: React Flow measures handles
      // and applies the fitted viewport after the reducer turns opacity on.
      const measured =
        width > 0 &&
        height > 0 &&
        nodes.length > 0 &&
        nodes.every(
          (node) =>
            // Section dividers have a measured height of zero after layout.
            // Defined dimensions distinguish them from unmeasured nodes.
            node.measured.width !== undefined &&
            node.measured.height !== undefined &&
            node.internals.handleBounds
        );
      const geometry = JSON.stringify([
        width,
        height,
        transform,
        nodes.map((node) => [
          node.id,
          node.measured,
          node.internals.positionAbsolute,
          node.internals.handleBounds,
        ]),
      ]);
      stableFrames =
        measured && geometry === previousGeometry ? stableFrames + 1 : 0;
      previousGeometry = geometry;
      if (stableFrames >= 2) {
        hasReportedReady.current = true;
        onReady();
        return;
      }
      frame = requestAnimationFrame(checkGeometry);
    };
    frame = requestAnimationFrame(checkGeometry);
    return () => cancelAnimationFrame(frame);
  }, [fontsReady, isInitialized, onReady, store]);

  return fontsReady;
}
